import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, inject, input, OnDestroy, OnInit, output, viewChild } from '@angular/core';
import { MatDrawer, MatDrawerContainer } from '@angular/material/sidenav';
import { GeneralLookupService } from '@app/cem/core-data/services/general-lookup.service';
import { ButtonDirective } from '@progress/kendo-angular-buttons';
import { DialogActionsComponent, DialogComponent } from '@progress/kendo-angular-dialog';
import { AgGridAngular } from 'ag-grid-angular';
import { FilterChangedEvent, SelectionChangedEvent } from 'ag-grid-community';
import { Subject } from 'rxjs-compat';
import { assetMappingTankBatteryGridOptions, assetMappingTankBatterySelectedGridOptions } from './columns';

@Component({
  selector: 'app-project-asset-mapping-tank-battery',
  templateUrl: './project-asset-mapping-tank-battery.component.html',
  styleUrls: ['./project-asset-mapping-tank-battery.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AgGridAngular,
    ButtonDirective,
    DialogActionsComponent,
    DialogComponent,
    MatDrawer,
    MatDrawerContainer,
  ],
})
export class ProjectAssetMappingTankBatteryComponent implements OnInit, AfterViewInit, OnDestroy {
  public generalLookupService = inject(GeneralLookupService);
  private cd = inject(ChangeDetectorRef);

  readonly assetTeamId = input<number>(undefined);
  readonly scenarioId = input<number>(undefined);
  readonly projectTypeId = input<number>(undefined);
  readonly projectSubTypeId = input<number>(undefined);
  readonly preSelectedAssets = input<Array<any>>([]);
  readonly notifyTBAssetChange = input<Subject<any>>(undefined);
  readonly projectSubType = input<string>(undefined);

  readonly tankBatteryAssets = output<any>();
  readonly clearTBAsset = output<any>();

  private readonly agSelectionGrid = viewChild<AgGridAngular>('agSelectionGrid');
  private readonly agDataGrid = viewChild<AgGridAngular>('agDataGrid');
  public readonly drawer = viewChild<MatDrawer>('drawer');

  public assetMappingTankBatteryGridOptions = assetMappingTankBatteryGridOptions;
  public assetMappingTankBatterySelectedGridOptions = assetMappingTankBatterySelectedGridOptions;
  private selectionTeamFilter = 'AssetTeamId eq';
  private selectionScenarioFilter = 'ScenarioId eq';
  public availableWells: any[] = [];
  public selectedWells: any[] = [];
  public openedWarning: boolean = false;
  public selectedTBAsset: any;

  constructor() {
    effect(() => {
      this.projectSubType();
      this.onAvailableGridReady(null);
    })
  }

  ngAfterViewInit(): void {
    this.drawer().toggle();
  }

  ngOnInit() {
    this.notifyTBAssetChange().subscribe(resp => {
      if (!resp) {
        return;
      }
      this.selectedTBAsset = resp;
      if (this.agDataGrid().api.getRenderedNodes().length > 0) {
        this.validateSDEOverride();
      }
      return false;
    });
  }

  ngOnDestroy() {
    this.notifyTBAssetChange().unsubscribe();
  }
  private formatSelectionQueryStringV2(): string {
    let genericFilter = '';
    let subTypeFilter = '';
    switch (this.projectSubType()) {
      case 'Initial Build':
      case 'Expansion':
        subTypeFilter = ` and (AssetAllocated eq false)`;
        break;
      case 'Compression':
        subTypeFilter = '';
        break;
    }
    let selectFilter = '&$select=AssetId,AssetName,Operator,TankBatteryName,FieldDevelopmentArea,AreaTeam,Ssn,SpudDate,RigReleaseDate,IsJointVenture,PutOnProductionDate,PadRigReleaseDate,BasinId,WellProfileId,SdeFacilityIdOverride,ActualDrillGroupReference';

    genericFilter += `(${this.selectionTeamFilter} ${this.assetTeamId()} and ${this.selectionScenarioFilter} ${this.scenarioId()} ${subTypeFilter})`;

    return `TankBatteryProjectAssetMappingView?$filter=${genericFilter} ${selectFilter}`;
  }

  onAvailableGridReady(params) {
    this.generalLookupService.getFiltered(this.formatSelectionQueryStringV2())
      .then(payload => {
        this.agSelectionGrid().api.setGridOption('rowData', payload.value);
        if (this.preSelectedAssets().length > 0) {
          let selectedNodes = payload.value.filter(x => this.preSelectedAssets().map(y => y.Ssn).includes(x.Ssn));
          this.agSelectionGrid().api.forEachNode(node => {
            if (selectedNodes.map(z => z.Ssn).includes(node.data.Ssn)) {
              node.setSelected(true);
            }
          });
          this.addSelectedRows();
        }
      });
  }

  onSelectedGridReady(params) {
    params.api.setGridOption('rowData', []);
  }

  onAvailableFilterChanged(event: FilterChangedEvent) {
    event.api.setGridOption('rowSelection', {
      mode: 'multiRow',
      headerCheckbox: true,
      selectAll: event.api.isAnyFilterPresent() ? 'filtered' : 'all',
    });
  }

  onAvailableSelectionChanged(params: SelectionChangedEvent) {
    this.availableWells = [];
    this.agSelectionGrid().api.getSelectedRows().map((x) => this.availableWells.push(x));
  }

  clearAvailableRows() {
    this.agSelectionGrid().api.deselectAll();
    this.availableWells = [];
  }

  clearSelectedRows() {
    this.agDataGrid().api.deselectAll();
    this.selectedWells = [];
  }

  onSelectedSelectionChanged(params: SelectionChangedEvent) {
    this.selectedWells = [];
    this.agDataGrid().api.getSelectedRows().map((x) => this.selectedWells.push(x));
  }

  addSelectedRows() {
    if (this.tankBatteryAssets && (this.projectSubType() == 'Initial Build' || this.projectSubType() == 'Expansion')) {
      return this.validateSDEOverride();
    }
    this.confirmSelection();
  }

  removeSelectedRows() {
    const agDataGrid = this.agDataGrid();
    this.agSelectionGrid().api.applyTransaction({ add: agDataGrid.api.getSelectedRows() });
    agDataGrid.api.applyTransaction({ remove: agDataGrid.api.getSelectedRows() });
    agDataGrid.api.deselectAll();
    this.emitData();
    this.selectedWells = [];
  }

  emitData() {
    let data = [];
    this.agDataGrid().api.forEachNode(node => data.push(node.data));
    this.tankBatteryAssets.emit(data);
  }

  validateSDEOverride() {
    let wells = this.agSelectionGrid().api.getSelectedRows();
    let selectedWells = this.agDataGrid().api.getRenderedNodes().map(x => x.data);
    wells = wells.concat(selectedWells);
    if (this.selectedTBAsset && wells.filter(x => x.SdeFacilityIdOverride > 0 &&
      x.SdeFacilityIdOverride !== this.selectedTBAsset.LegacyAssetId).length > 0) {
      setTimeout(() => {
        this.openedWarning = true;
        this.cd.detectChanges();
      })
    }
    else
      this.confirmSelection();
  }

  onWarningClose() {
    this.openedWarning = false;
    this.agSelectionGrid().api.deselectAll();
    this.clearTBAsset.emit(true);
  }

  onWarningSave() {
    this.confirmSelection();
    this.openedWarning = false;
  }

  confirmSelection() {
    const agSelectionGrid = this.agSelectionGrid();
    this.agDataGrid().api.applyTransaction({ add: agSelectionGrid.api.getSelectedRows() });
    agSelectionGrid.api.applyTransaction({ remove: agSelectionGrid.api.getSelectedRows() });
    agSelectionGrid.api.deselectAll();
    this.emitData();
    this.availableWells = [];
  }
}